import {
  ActivityThresholdSeconds,
  TypingThresholdSeconds,
} from "@/types/presence"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { now, secondsBetween } from "../date-utils"
import { updateCollaboratorsCache } from "./api/trips-api"
import { announcePresenceChange } from "./collaboration-thunks"
import {
  removeUserPresence,
  selectCurrentUserPresenceStatus,
  selectIdle,
  selectRefsOfUsersWhoArePresent,
  selectUserPresenceStatuses,
  updateUserPresence,
} from "./presence-slice"
import { AppDispatch, RootState } from "./redux-store"
import tickerSlice from "./ticker-slice"
import { selectTripRef } from "./trip-slice"
import { selectUserIsAnonymous } from "./user-slice"

// A thunk for periodic actions triggered by the passage of time.
// This thunk will fire approximately every minute.
export const tick = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("tickerThunks/tick", async (_args, { dispatch, getState }) => {
  const nowTimestamp = now()

  // Update our record of when we last fired, useful for
  // displaying elapsed times.
  dispatch(tickerSlice.actions.setNowNearestMinute(nowTimestamp))

  // Stop here if user is anonymous
  if (selectUserIsAnonymous(getState())) return

  // Let collaborators know if we're active.
  const idle = selectIdle(getState())
  if (!idle) {
    // TODO: We want to announce our presence and say whether we're typing
    // or just generally active. We'd do a better job of refreshing the
    // typing case if we knew the time of the last char entered into the
    // group chat form. Too much trouble to maintain?
    const currentUserStatus = selectCurrentUserPresenceStatus(getState())
    const presenceType =
      currentUserStatus?.presence_type === "typing" &&
      secondsBetween(currentUserStatus.timestamp, nowTimestamp) <
        TypingThresholdSeconds
        ? "typing"
        : "active"
    void dispatch(announcePresenceChange({ presenceType }))
  }

  // Take a look at collaborators from whom we've seen activity
  // in the past. Have any been idle long enough that we should
  // consider them no longer active?
  selectUserPresenceStatuses(getState()).forEach((status) => {
    const secondsSinceActivityDetected = secondsBetween(
      status.timestamp,
      nowTimestamp
    )
    if (secondsSinceActivityDetected < TypingThresholdSeconds) {
      // nothing to do
    } else if (status.presence_type === "typing") {
      // Assume user is no longer typing but still active.
      dispatch(
        updateUserPresence({
          user_ref: status.user_ref,
          presence_type: "active",
          timestamp: nowTimestamp,
        })
      )
    } else if (secondsSinceActivityDetected > ActivityThresholdSeconds) {
      dispatch(removeUserPresence(status.user_ref))

      const tripRef = selectTripRef(getState())
      const userRefs = selectRefsOfUsersWhoArePresent(getState())
      if (tripRef) {
        updateCollaboratorsCache(dispatch, tripRef, userRefs)
      }
    }
  })
})
